import React, { useEffect, useState } from 'react';
// The loader comes from '/pure' and the type from the package entry, and they must stay split:
// the main entry injects the Stripe.js script as a side effect of being imported, while a
// type-only import is erased by TypeScript and adds no runtime module at all.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useCompany } from '../../hooks/company';
import LoadingPage from '../../pages/LoadingPage';

const CompanyStripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { company, loading: companyLoading, loadCompany } = useCompany();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    if (!company?.id) {
      loadCompany();
    }
  }, [company, loadCompany]);

  useEffect(() => {
    // Two things kept Stripe.js on every page. The plain `@stripe/stripe-js` entry injects the
    // script as a side effect of the import, so gating this call alone would have deferred
    // nothing - hence `/pure`, which injects only when loadStripe is first called. And the guard
    // below was commented out, so this ran as loadStripe(undefined) for every tenant whose API
    // payload omits the key, which is what the API does whenever Stripe is disabled.
    //
    // The condition is the one src/pages/ProductsPage already gates the wallet button on. If the
    // key only arrives later, the dependency below re-runs the effect and the load happens then.
    if (company?.stripePublishableKey) {
      setStripePromise(loadStripe(company.stripePublishableKey));
    }
  }, [company?.stripePublishableKey]);

  if (companyLoading) {
    // Only the screen: on `/hash` the route's LoadingPage, mounted once the company is loaded, is the
    // one that starts the table session.
    return <LoadingPage skipTableLoad />;
  }
  return <Elements stripe={stripePromise}>{children}</Elements>;
};

export default CompanyStripeProvider;
